import styles from './completeShipmentItemsTable.module.css'
import { useEffect, useMemo, useState } from "react"
import { Column, FieldType, FieldValue, NumberField, O, Section, Table } from "../../../../../features/ui"
import { Item, Operation } from "../../../../operations"
import { Lot, Product, ProductName, ProductType, useLotLoaders, useProductLoaders } from "../../../../products"
import { isDefined } from '../../../../../utils/typeUtils'
import { CompleteShipmentItemRow } from '../../types'

type Props = {
    operation: Operation
    defaultRows: CompleteShipmentItemRow[]
    title?: string
    onChange: (items: CompleteShipmentItemRow[]) => void
}

export const CompleteShipmentItemsTable = (props: Props) => {
    const { operation, defaultRows, title, onChange } = props

    const [products, setProducts] = useState<Product[]>([])
    const [lots, setLots] = useState<Lot[]>([])
    const [rows, setRows] = useState<CompleteShipmentItemRow[]>(defaultRows)

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    const load = async (items: Item[]) => {
        const { loadProducts } = productLoaders()
        const loadedProducts = await loadProducts(items.map(item => item.productId))
        setProducts(loadedProducts)
        const { loadProductsLots } = lotLoaders()
        const loadedLots = await loadProductsLots(loadedProducts.map(product => product.id))
        setLots(loadedLots)
    }

    useEffect(() => {
        load(operation.items)
    }, [operation.id])

    useEffect(() => {
        setRows(defaultRows)
    }, [defaultRows])

    useEffect(() => {
        onChange(rows)
    }, [rows])

    const onChangeDeliveredQuantity = (itemId: string) => (value?: FieldValue) => {
        const newDeliveredQuantity = value as O<number>
        if (!isDefined(newDeliveredQuantity)) return
        setRows(rows.map(row => row.itemId === itemId ? {
            ...row,
            deliveredQuantity: newDeliveredQuantity!,
            notDeliveredQuantity: row.dispatched - newDeliveredQuantity!
        } : row))
    }

    const onChangeNotDeliveredQuantity = (itemId: string) => (value?: FieldValue) => {
        const newNotDeliveredQuantity = value as O<number>
        if (!isDefined(newNotDeliveredQuantity)) return
        setRows(rows.map(row => row.itemId === itemId ? {
            ...row,
            notDeliveredQuantity: newNotDeliveredQuantity!,
            deliveredQuantity: row.dispatched - newNotDeliveredQuantity!
        } : row))
    }

    const onClickQuantity = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation()
    }

    const columns: Column[] = useMemo(() => [
        {
            name: 'itemId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                const lot = lots.find(lot => lot.id === row.lotId)
                return <ProductName product={product} lot={lot} showSupplierCode />
            }
        },
        {
            name: 'dispatched',
            type: FieldType.NUMBER,
            label: 'Despachado'
        },
        {
            name: 'deliveredQuantity',
            type: FieldType.NUMBER,
            label: 'Entregado',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                return (
                    <NumberField
                        id="deliveredQuantity"
                        value={row.deliveredQuantity}
                        integer={product?.type !== ProductType.BULK}
                        min={0}
                        max={row.dispatched}
                        className={styles.qtyField}
                        onChange={onChangeDeliveredQuantity(row.itemId)}
                        onClick={onClickQuantity}
                    />
                )
            }
        },
        {
            name: 'notDeliveredQuantity',
            type: FieldType.NUMBER,
            label: 'No Entregado',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                return (
                    <NumberField
                        id="notDeliveredQuantity"
                        value={row.notDeliveredQuantity}
                        integer={product?.type !== ProductType.BULK}
                        min={0}
                        max={row.dispatched}
                        className={styles.qtyField}
                        onChange={onChangeNotDeliveredQuantity(row.itemId)}
                        onClick={onClickQuantity}
                    />
                )
            }
        }
    ], [rows, products, lots])

    const tableRows = useMemo(() => rows.map(row => ({
        itemId: row.itemId,
        dispatched: row.dispatched,
        deliveredQuantity: row.itemId,
        notDeliveredQuantity: row.itemId
    })), [rows])

    return (
        <Section
            id="complete-shipment-items"
            title={title || "Productos"}
            space={12}
        >
            <Table
                columns={columns}
                rows={tableRows}
                rowId="itemId"
                readOnly
                alignRight={false}
            />
        </Section>
    )
}
